// src/utils/terrainUtils.js
import * as THREE from 'three'; // Importa THREE para usar THREE.Color

// Función para obtener datos de elevación de AWS Terrain Tiles
export async function fetchElevationData(lat, lon, zoom = 10) {
  try {
    // Calcular las coordenadas del tile (x, y) en el sistema de Mercator
    const tileX = Math.floor((lon + 180) / 360 * Math.pow(2, zoom));
    const tileY = Math.floor((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom));
    
    // URL del AWS Terrain Tiles
    // Los tiles de Terrarium son PNG de 256x256 píxeles
    const url = `https://s3.amazonaws.com/elevation-tiles-prod/terrarium/${zoom}/${tileX}/${tileY}.png`;
    
    console.log(`Intentando cargar tile: ${url}`);
    
    // Crear una imagen y cargar los datos
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Es crucial para evitar problemas de CORS al leer píxeles del canvas
    
    return new Promise((resolve, reject) => {
      img.onload = () => {
        try {
          // Crear un canvas temporal para procesar la imagen del tile
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;
          
          ctx.drawImage(img, 0, 0); // Dibuja la imagen en el canvas
          // Obtiene los datos de píxeles (RGBA) de la imagen
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data; // Array de píxeles [R, G, B, A, R, G, B, A, ...]
          
          // Convertir RGB a elevación usando la fórmula del formato Terrarium
          const elevationData = [];
          for (let i = 0; i < data.length; i += 4) { // Avanza de 4 en 4 (RGBA)
            const r = data[i];     // Componente Rojo
            const g = data[i + 1]; // Componente Verde
            const b = data[i + 2]; // Componente Azul
            
            // Fórmula Terrarium: altura = (R * 256 + G + B / 256) - 32768
            // Esta fórmula decodifica la elevación codificada en los canales RGB
            const elevation = (r * 256 + g + b / 256) - 32768;
            elevationData.push(elevation);
          }
          
          resolve(elevationData); // Resuelve la promesa con los datos de elevación
        } catch (error) {
          reject(new Error(`Error procesando imagen del tile: ${error.message}`));
        }
      };
      
      img.onerror = () => {
        reject(new Error(`No se pudo cargar el tile de elevación desde AWS. Verifique la URL o la conectividad.`));
      };
      
      img.src = url; // Establece la URL de la imagen para iniciar la carga
    });
    
  } catch (error) {
    throw new Error(`Error en fetchElevationData: ${error.message}`);
  }
}

// Función para generar datos simulados basados en la ubicación
// Esto se usa como fallback si no se pueden cargar los datos reales de AWS
export function generateLocationSpecificData(lat, lon, width, height) {
  const elevationData = [];
  const centerX = width / 2;
  const centerY = height / 2;
  
  // Factores basados en la ubicación geográfica para simular diferentes terrenos
  const latFactor = Math.abs(lat) / 90; // 0-1 basado en distancia del ecuador (más cerca del ecuador, menor factor)
  const lonFactor = Math.sin(lon * Math.PI / 180); // -1 a 1, para variar con la longitud
  
  // Determinar tipo de terreno basado en coordenadas (simplificado)
  let baseElevation = 0;
  let variation = 100; // Variación base en la altura
  
  if (Math.abs(lat) > 60) {
    // Regiones polares: más planas, elevación baja
    baseElevation = 50;
    variation = 150;
  } else if (Math.abs(lat) < 23.5) {
    // Trópicos: más variación (montañas, valles)
    baseElevation = 200;
    variation = 300;
  } else if (Math.abs(lon) > 100) {
    // Regiones "montañosas" simuladas (ej. Andes, Himalayas, Rocosas)
    baseElevation = 500;
    variation = 800;
  }
  
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      // Distancia desde el centro del tile
      const distanceFromCenter = Math.sqrt(
        Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2)
      );
      
      // Distancia normalizada (0 a 1)
      const normalizedDistance = distanceFromCenter / (Math.max(width, height) / 2);
      
      // Combinar múltiples funciones de ruido (simulación de terreno irregular)
      const noise1 = Math.sin(x * 0.05) * Math.cos(y * 0.05);
      const noise2 = Math.sin(x * 0.02 + lat) * Math.cos(y * 0.02 + lon);
      const noise3 = Math.sin(x * 0.1) * Math.sin(y * 0.1);
      
      const combinedNoise = (noise1 + noise2 * 0.5 + noise3 * 0.3) / 1.8; // Promedio ponderado
      
      // Aplicar factores geográficos para influir en la elevación
      const geographicFactor = (latFactor + Math.abs(lonFactor)) / 2;
      
      const elevation = baseElevation + 
                        (combinedNoise * variation * (1 - normalizedDistance * 0.3)) + // Ruido con atenuación hacia los bordes
                        (geographicFactor * variation * 0.5); // Influencia geográfica
      
      elevationData.push(Math.max(0, elevation)); // Asegura que la elevación no sea negativa
    }
  }
  
  return elevationData;
}

// Función para obtener color basado en elevación
export function getColorForElevation(elevation, minElevation, maxElevation) {
  // Normaliza la elevación a un rango de 0 a 1
  const normalizedElevation = (elevation - minElevation) / (maxElevation - minElevation);

  // Define un gradiente de colores para diferentes rangos de elevación
  // Los valores son [valor_normalizado, color_hex]
  const colors = [
    [0.0, '#0a0a55'],   // Azul oscuro (profundidades marinas)
    [0.05, '#1e1e80'],  // Azul marino
    [0.15, '#2a52be'],  // Azul más claro (aguas poco profundas)
    [0.25, '#4682b4'],  // Azul acero
    [0.35, '#87ceeb'],  // Azul cielo (costas)
    [0.45, '#98fb98'],  // Verde claro (llanuras, valles)
    [0.50, '#32cd32'],  // Verde medio (bosques)
    [0.60, '#6b8e23'],  // Verde oliva (colinas)
    [0.70, '#a5a134'],  // Marrón claro (terreno árido)
    [0.80, '#f0df28'],  // Amarillo (altas mesetas)
    [0.90, '#fb6a09'],  // Naranja (montañas rocosas)
    [1.0, '#db1919']    // Rojo oscuro (picos nevados o muy altos)
  ];


  // Función auxiliar para convertir hex a RGB (0-1)
  function hexToRgb(hex) {
    const bigint = parseInt(hex.slice(1), 16);
    return {
      r: (bigint >> 16) / 255,
      g: ((bigint >> 8) & 255) / 255,
      b: (bigint & 255) / 255
    };
  }

  // Interpola el color linealmente entre los puntos de color definidos
  for (let i = 0; i < colors.length - 1; i++) {
    const [startValue, startColorHex] = colors[i];
    const [endValue, endColorHex] = colors[i + 1];

    if (normalizedElevation >= startValue && normalizedElevation <= endValue) {
      const t = (normalizedElevation - startValue) / (endValue - startValue);

      const startRGB = hexToRgb(startColorHex);
      const endRGB = hexToRgb(endColorHex);

      // Retorna un objeto THREE.Color interpolado
      return new THREE.Color(
        startRGB.r + (endRGB.r - startRGB.r) * t,
        startRGB.g + (endRGB.g - startRGB.g) * t,
        startRGB.b + (endRGB.b - startRGB.b) * t
      );
    }
  }

  // Si la elevación es exactamente el máximo, retorna el último color
  return new THREE.Color(hexToRgb(colors[colors.length - 1][1]));
}

// Helper function para calcular los límites del área 3D
// Se movió aquí desde MapboxMap.jsx para ser una utilidad general
export function getTerrainBounds(lat, lon, zoomLevel = 10) {
  const tileSizeDegrees = 360 / Math.pow(2, zoomLevel);
  const halfSizeDegrees = tileSizeDegrees / 2;

  return {
    north: lat + halfSizeDegrees,
    south: lat - halfSizeDegrees,
    east: lon + halfSizeDegrees,
    west: lon - halfSizeDegrees
  };
}
